import { Paper, Button, Container, Stack, Typography, Dialog, IconButton, Box } from '@mui/material';
import { FormProvider, SelectTag, Table } from '../../../../../../components';
import { useForm } from 'react-hook-form';
import { request, toast, dropdownFetch, getKeyPathValue } from '../../../../../../utils';
import { useCallback, useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { OPDTypeAmountForm } from './opdTypeAmountForm';
import { CustomButtonn } from '../../../../../../components';
import { useLocation } from 'react-router-dom';

const OPD_TYPE_TABLE_HEADER = [
  { header: 'OPD Type', accessor: 'name' },
  { header: 'Discount', accessor: 'discount' },
  { header: 'Amount', accessor: 'amount' },
  { header: 'Other Charge', accessor: 'other_charge' }
];

const TABLE_COLUMNS = [
  {
    header: 'Doctor Name',
    accessor: (row) => {
      const status = row.status;
      return (
        <span
          style={{
            color: status === 'inactive' ? 'red' : 'black'
          }}
        >
          {`${row.doctor.name}`}
        </span>
      );
    }
  },
  { header: 'Sponsor Name', accessor: 'sponsor.name' },
  { header: 'Status', accessor: 'status', sx: { textTransform: 'uppercase' } },
  { header: 'Created At', accessor: 'createdAt' }
];

const initialFeeDataObj = {
  data: [],
  loading: false
};
export const DoctorFeeSetup = () => {
  const [currentAction, setCurrentAction] = useState();
  const [feeDataObj, setFeeDataObj] = useState(initialFeeDataObj);
  const [loading, setLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState();
  const [opdDetailList, setOpdDetailList] = useState([]);
  const [openOpdDetailListDialog, setOpenDetailListDialog] = useState(false);
  const [openQueryDialog, setOpenQueryDialog] = useState(false);
  const [sponsorDropdown, setSponsorDropdown] = useState([]);
  const [doctorDropdown, setDoctorDropdown] = useState([]);
  const [opdTypeDropdown, setOpdTypeDropdown] = useState([]);
  const { pathname } = useLocation();
  const lastPathSegment = pathname.split('/').pop();

  const getOpdTypeDropdown = useCallback(async () => {
    const data = await dropdownFetch('opd-type');
    setOpdTypeDropdown(data);
  }, []);
  const getSponsorDropdown = useCallback(async () => {
    const data = await dropdownFetch('sponsor');
    setSponsorDropdown(data);
  }, []);
  const getDoctorDropdown = useCallback(async () => {
    const data = await dropdownFetch('doctor');
    setDoctorDropdown(data);
  }, []);

  useEffect(() => {
    getSponsorDropdown();
    getDoctorDropdown();
    getOpdTypeDropdown();
  }, [getSponsorDropdown, getOpdTypeDropdown, getDoctorDropdown]);

  const methods = useForm({ defaultValues: {}, mode: 'all' });
  const { handleSubmit, watch, setValue, reset } = methods;

  useEffect(() => {
    if (selectedRecord) {
      const keyValuePair = getKeyPathValue(selectedRecord, '', ['opd_type_fee', '_id', '__v', 'createdAt', 'status']);
      keyValuePair.forEach(({ key, value }) => setValue(key, value));
      setOpdDetailList(selectedRecord.opd_type_fee);
    } else {
      reset();
    }
  }, [selectedRecord, setValue, reset]);

  const formSubmit = async (formValues) => {
    setLoading(true);
    let editFlag = false;
    if (selectedRecord && currentAction === 'edit') {
      editFlag = true;
    }
    const payload = {
      ...formValues,
      opd_type_fee: opdDetailList.map((data) => ({
        amount: data.amount,
        discount: data.discount,
        opd_type: data.opd_type,
        other_charge: data.other_charge
      }))
    };
    const response = await request(editFlag ? '/doctor-fee-setup-update' : '/doctor-fee-setup-create', {
      method: editFlag ? 'PUT' : 'POST',
      params: editFlag ? selectedRecord._id : null,
      body: payload
    });
    if (response?.success) {
      toast(`Successfully ${editFlag ? 'updated' : 'created'} Doctor Fees Setup`, { variant: 'success' });
      setSelectedRecord(response.data);
      setCurrentAction();
      reset();
      setOpdDetailList([]);
    } else {
      toast(`Failed to ${editFlag ? 'update' : 'create'} Doctor Fees Setup`, {
        variant: 'error'
      });
    }
    setLoading(false);
  };

  const handleQueryWindow = async () => {
    setOpenQueryDialog(true);
    setFeeDataObj({ loading: true });
    const response = await request('/doctor-fee-setup-list', { method: 'GET' });
    if (response.success) {
      const filteredData = response.data.filter(
        (item) => item.status === 'active' || item.status === 'inactive' || item.status === 'default'
      );
      setFeeDataObj({ loading: false, data: filteredData });
    } else {
      toast('Error in fetching data', { variant: 'error' });
      setFeeDataObj({ loading: false });
    }
  };

  const handleCancelRecord = async () => {
    setLoading(true);
    const response = await request('/doctor-fee-setup-cancel', { method: 'DELETE', params: selectedRecord._id });
    if (response?.success) {
      toast('Successfully Cancelled Doctor Fee Type', { variant: 'success' });
      setOpdDetailList([]);
      setSelectedRecord();
    } else {
      toast('Failed to Cancelled Doctor Fee Type', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleDeleteRecord = async () => {
    setLoading(true);
    const response = await request('/doctor-fee-setup-delete', { method: 'DELETE', params: selectedRecord._id });
    if (response?.success) {
      toast('Successfully deleted Doctor Fee Type', { variant: 'success' });
      setOpdDetailList([]);
      setSelectedRecord();
    } else {
      toast('Failed to delete Doctor Fee Type', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleRowDelete = (rowId) => {
    setOpdDetailList((prev) => prev.filter((data) => data.opd_type !== rowId));
  };
  return (
    <>
      <Paper
        sx={{
          p: 0,
          flex: 1,
          borderRadius: 0,
          boxShadow: 'none',
          width: '100%',
          height: 'auto'
        }}
      >
        <Container
          sx={{
            mt: 1,
            border: '0px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: 'none',
            minWidth: '100%',
            boxSizing: 'border-box'
          }}
        >
          <FormProvider methods={methods} onSubmit={handleSubmit(formSubmit)}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                mb: 1,
                borderBottom: '0px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3" sx={{ fontWeight: 600 }}>
                Doctor Fee Setup
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                <CustomButtonn
                  id={`${lastPathSegment}Write`}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction)}
                  onClick={() => {
                    setSelectedRecord();
                    setCurrentAction('new');
                    reset();
                    setOpdDetailList([]);
                  }}
                >
                  New
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Update`}
                  variant="contained"
                  disabled={['new', 'edit'].includes(currentAction) || !selectedRecord}
                  onClick={() => setCurrentAction('edit')}
                >
                  Edit
                </CustomButtonn>
                <CustomButtonn
                  id={'Submit'}
                  type="submit"
                  variant="contained"
                  disabled={!['edit', 'new'].includes(currentAction) || loading}
                >
                  Save
                </CustomButtonn>
                <CustomButtonn
                  id={'Cancel'}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction) || loading}
                  onClick={() => {
                    setCurrentAction();
                    handleCancelRecord();
                  }}
                >
                  Cancel
                </CustomButtonn>
                <CustomButtonn
                  id={`isSuperAdmin`}
                  variant="contained"
                  disabled={!selectedRecord || ['new', 'edit'].includes(currentAction) || loading}
                  onClick={handleDeleteRecord}
                >
                  Delete
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Read`}
                  variant="contained"
                  onClick={handleQueryWindow}
                  disabled={['new', 'edit'].includes(currentAction)}
                >
                  Query
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Print`}
                  variant="contained"
                  disabled={!selectedRecord || currentAction}
                >
                  Print
                </CustomButtonn>
                <CustomButtonn
                  id={'Exit'}
                  variant="contained"
                  onClick={() => {
                    setCurrentAction('');
                  }}
                >
                  Exit
                </CustomButtonn>
              </Stack>
            </Stack>
            <Box
              sx={{
                border: '0px solid gray',
                padding: 2,
                borderRadius: '8px',
                width: '80%',
                boxShadow: '0px 2px 10px #0099ff'
              }}
            >
              <Stack spacing={2} direction="row" sx={{ mb: 2 }}>
                <Stack sx={{ flex: 3 }} spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Sponsor Name</Typography>
                  <SelectTag
                    formControlSX={{ flex: 0.8, border: currentAction ? '0.5px solid red' : '' }}
                    disabled={!['new', 'edit'].includes(currentAction)}
                    name="sponsor"
                    menus={sponsorDropdown}
                    placeholder="Select Sponsor"
                  />
                </Stack>
                <Stack sx={{ flex: 3 }} spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Doctor</Typography>
                  <SelectTag
                    formControlSX={{ flex: 0.8, border: currentAction ? '0.5px solid red' : '' }}
                    disabled={!['new', 'edit'].includes(currentAction)}
                    name="doctor"
                    menus={doctorDropdown}
                    placeholder="Select Doctor"
                  />
                </Stack>
              </Stack>
              <Stack spacing={2} sx={{ mb: 2 }}>
                <Stack spacing={1} direction="row" alignItems="center" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    disabled={!['new', 'edit'].includes(currentAction) || !watch('doctor') || !watch('sponsor')}
                    onClick={() => setOpenDetailListDialog(true)}
                  >
                    Add Details
                  </Button>
                </Stack>
                <Table
                  containerSX={{ flex: 1, border: '0px solid rgba(0,0,0,0.27)' }}
                  data={opdDetailList.map((data) => ({ _id: data.opd_type, ...data }))}
                  columns={OPD_TYPE_TABLE_HEADER}
                  showDeleteAction={['new', 'edit'].includes(currentAction)}
                  onDeleteHandler={handleRowDelete}
                />
              </Stack>
            </Box>
          </FormProvider>
        </Container>
      </Paper>
      <Dialog open={openOpdDetailListDialog} onClose={() => setOpenDetailListDialog(false)} scroll="body" maxWidth="xl">
        <OPDTypeAmountForm
          onClose={() => setOpenDetailListDialog(false)}
          onAdd={(data) => {
            const [existingDetail] = opdDetailList.filter(({ opd_type }) => opd_type === data.opd_type);
            if (existingDetail) {
              toast('OPD Type Service already chosen', { variant: 'error' });
              return;
            }
            const opdTypeName = opdTypeDropdown.filter(({ _id }) => _id === data.opd_type)[0]?.type;
            setOpdDetailList((prev) => [...prev, { ...data, _id: data.opd_type, name: opdTypeName }]);
          }}
          opdTypeDropdown={opdTypeDropdown}
        />
      </Dialog>
      <Dialog
        open={openQueryDialog}
        onClose={() => setOpenQueryDialog(false)}
        PaperProps={{ sx: { width: '80%', p: 1.5 } }}
        maxWidth="xl"
        scroll="body"
      >
        <Stack
          padding={0.2}
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor="grey.300"
        >
          <Typography variant="h4" fontWeight="bold">
            Doctor Fees Setup Query Window
          </Typography>
          <IconButton onClick={() => setOpenQueryDialog(false)}>
            <CloseIcon color="error" fontSize="large" />
          </IconButton>
        </Stack>
        <Table
          sx={{ mt: 0.5 }}
          columns={TABLE_COLUMNS}
          data={feeDataObj.data || []}
          loading={feeDataObj.loading}
          onRowClick={(row) => {
            const { doctor, sponsor, opd_type_fee, ...others } = row;
            setSelectedRecord({
              ...others,
              doctor: doctor._id,
              sponsor: sponsor._id,
              opd_type_fee: opd_type_fee.map((data) => ({
                ...data,
                opd_type: data.opd_type._id,
                name: data.opd_type.type
              }))
            });
            setOpenQueryDialog(false);
          }}
        />
      </Dialog>
    </>
  );
};
